import React, { Fragment, useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Typography, MenuItem, Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import AppState from '../../../models/redux/state/app.state';
import {
  patchProfileAPIcall,
  patchCurrentUserAPIcall,
  getUserByUrlAPIcall,
} from '../../../redux/actions/auth/auth.actions';
import ProfileFormValues from '../../../models/profile/ProfileFormValues';
import TextFieldWrapper from '../../micro/TextFieldWrapper/TextFieldWrapper';
import HomeNavBar from '../../micro/HomeNavBar/HomeNavBar';
import HomeFooter from '../../micro/HomeFooter/HomeFooter';
import { BASE_URL, IMAGE_PATHS } from '../../../constants/paths.constants';
import { lightGrey } from '../../../app/theme';
import { getRolesAPIcall, getStatesAPIcall } from '../../../redux/actions/enums/enums.actions';
import { getProfileAPIcall } from '../../../redux/actions/profile/profile.actions';
import PasswordModal from '../../micro/PasswordModal/PasswordModal';
import history from '../../../services/browserHistory';
import { getTrainingAPIcall, patchTrainingAPIcall } from '../../../redux/actions/training/training.actions';

const useStyles = makeStyles((theme: Theme) => ({
  formContainer: {
    padding: '17px 65px 65px',
    minHeight: 'calc(100vh - 235px)',
  },
  pageName: {
    color: theme.palette.secondary.main,
    fontWeight: 600,
    fontSize: 18,
  },
  title: {
    margin: '17px 0 49px 0',
    paddingBottom: '0.5em',
    borderBottom: `1px solid ${theme.palette.secondary.main}`,
    display: 'inline-block',
    minWidth: 465,
    color: theme.palette.secondary.main,
    fontSize: 40,
    fontWeight: 600,
  },
  columnsWrapper: {
    display: 'flex',
    flexDirection: 'row',
  },
  formColumn: {
    width: '25%',
    marginRight: '3%',
    display: 'flex',
    flexDirection: 'column',
  },
  inputField: {
    color: lightGrey,
    textTransform: 'uppercase',
  },
  control: {
    marginBottom: 4,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  smallControlsContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  smallControls: {
    width: '45%',
    '& > div': {
      width: '100%',
    },
  },
  buttons: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 16,
    margin: 0,
  },
  button: {
    borderRadius: 21,
    height: 42,
    minWidth: 104,
    marginLeft: 37,
    width: 'auto',
  },
  passwordButton: {
    margin: '6px 0 0 0',
  },
  passwordInfo: {
    fontSize: 16,
    margin: 0,
  },
  list: {
    margin: 0,
    padding: 0,
    '& > li': {
      fontSize: 16,
      marginLeft: '1em',
    },
  },
  images: {
    display: 'block',
    width: '100%',
  },
  note: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
  },
}));

const phoneRegExp = /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;

const profileSchema = Yup.object({
  firstName: Yup.string()
    .transform((value) => value.trim())
    .min(2, 'First name must be at least 2 characters')
    .matches(/^(?=(?:.*[A-Za-z]){2,})[A-Za-z\s\-']+$/, 'First name must contain only alphabetic characters')
    .required('*Required field'),
  lastName: Yup.string()
    .transform((value) => value.trim())
    .min(2, 'Last name must be at least 2 characters')
    .matches(/^(?=(?:.*[A-Za-z]){2,})[A-Za-z\s\-']+$/, 'Last name must contain only alphabetic characters')
    .required('*Required field'),
  email: Yup.string().required('*Required field'),
  city: Yup.string()
    .transform((value) => value.trim())
    .min(3, 'City name must be at least 3 characters')
    .matches(/^(?=(?:.*[A-Za-z]){2,})[A-Za-z\s\-']+$/, 'City name must contain only alphabetic characters')
    .required('*Required field'),
  state: Yup.string().required('*Required field'),
  institutionName: Yup.string()
    .transform((value) => value.trim())
    .min(3, 'Institution name must be at least 3 characters')
    .matches(/^(?=(?:.*[A-Za-z]){3,})[A-Za-z0-9\s\-']+$/, 'Institution name must contain at least 3 letters')
    .required('*Required field'),
});

const ProfilePage = (): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const user = useSelector((state: AppState) => state.auth.user);
  const trainingInfo = useSelector((state: AppState) => state.training.current);
  const profile = useSelector((state: AppState) => state.profile.current);
  const roles = useSelector((state: AppState) => state.enums.roles);
  const states = useSelector((state: AppState) => state.enums.states);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [defaultFormValues, setDefaultFormValues] = useState<ProfileFormValues>({
    firstName: '',
    lastName: '',
    email: '',
    institutionName: '',
    city: '',
    state: '',
  });

  console.log('PROFILE PAGE auth.user =', user);
  console.log('PROFILE PAGE profile.current =', profile);

  useEffect(() => {
    document.title = 'My Profile';
    if (!user) {
      return;
    }
    if (!roles.length) {
      dispatch(getRolesAPIcall());
    }
    if (!states.length) {
      dispatch(getStatesAPIcall());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      setDefaultFormValues((prev) => ({
        ...prev,
        ...(user.firstName && { firstName: user.firstName }),
        ...(user.lastName && { lastName: user.lastName }),
        email: user.email,
      }));

      if (!profile || profile.url !== user.profile || !profile.isCompleted) {
        dispatch(getProfileAPIcall(user.profile));
      }

      if (!trainingInfo) {
        // TODO, 1 is for Axumin. Still have to make this more generic
        dispatch(getTrainingAPIcall('1'));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (profile) {
      setDefaultFormValues((prev) => ({
        ...prev,
        institutionName: profile.institutionName || '',
        city: profile.city || '',
        state: profile.state || '',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);
  const goBack = () => {
    history.goBack();
  };

  const handleSubmit = async (formValues: ProfileFormValues) => {
    if (user) {
      const trim = (value: string) => value.trim().replace(/\s{2,}/g, ' ');

      const trimmedValues = {
        firstName: trim(formValues.firstName),
        lastName: trim(formValues.lastName),
        email: formValues.email,
        city: trim(formValues.city),
        state: formValues.state,
        institutionName: trim(formValues.institutionName),
      };

      await dispatch(
        patchCurrentUserAPIcall(user.url, {
          firstName: trimmedValues.firstName,
          lastName: trimmedValues.lastName,
        }),
      );

      if (!profile?.isCompleted && trainingInfo) {
        trainingInfo.trainings.forEach((training) => {
          if (training.status.id === 1) {
            dispatch(patchTrainingAPIcall(training.url, undefined, undefined, 2));
          }
        });
      }

      // use any so can delete values
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const body: any = {
        ...formValues,
        institutionName: trimmedValues.institutionName,
        city: trimmedValues.city,
        state: formValues.state,
        isCompleted: true,
      };

      delete body.firstName;
      delete body.lastName;
      delete body.email;

      await dispatch(patchProfileAPIcall(user.profile, body));
      await dispatch(getUserByUrlAPIcall(user.url));
      await dispatch(getProfileAPIcall(user.profile));
      goBack();
    }
  };

  return (
    <Fragment>
      <HomeNavBar />
      <div className={classes.formContainer}>
        <Typography variant="h1" className={classes.title}>
          My Profile
        </Typography>
        <Typography variant="body1" className={classes.note}>
          *Required Field
        </Typography>
        <Formik
          initialValues={defaultFormValues}
          validationSchema={profileSchema}
          onSubmit={async (values, formikHelpers) => {
            formikHelpers.setTouched({
              firstName: true,
              lastName: true,
              email: true,
              city: true,
              state: true,
              institutionName: true,
            });

            const errors = await formikHelpers.validateForm();
            if (Object.keys(errors).length > 0) {
              console.warn('Validation error:', errors);
              return;
            }

            await handleSubmit(values);
          }}
          enableReinitialize
          validateOnChange={true}
          validateOnBlur={true}
        >
          {(formProps) => (
            <form onSubmit={formProps.handleSubmit}>
              <div className={classes.columnsWrapper}>
                <div className={classes.formColumn}>
                  <div className={classes.control}>
                    <TextFieldWrapper
                      label="FIRST NAME*"
                      inputLabelProps={{ className: classes.inputField }}
                      form={formProps}
                      formKey="firstName"
                      type="text"
                      variant="outlined"
                    />
                  </div>
                  <div className={classes.control}>
                    <TextFieldWrapper
                      label="LAST NAME*"
                      inputLabelProps={{ className: classes.inputField }}
                      form={formProps}
                      formKey="lastName"
                      type="text"
                      variant="outlined"
                    />
                  </div>
                  <div className={classes.control}>
                    <TextFieldWrapper
                      label="EMAIL ADDRESS*"
                      inputLabelProps={{ className: classes.inputField }}
                      form={formProps}
                      formKey="email"
                      type="text"
                      variant="outlined"
                      disabled
                    />
                  </div>
                  <div className={classes.passwordButton}>
                    <Button
                      tabIndex={-2}
                      className={classNames(classes.button, classes.passwordButton)}
                      variant="contained"
                      color="secondary"
                      onClick={() => setIsPasswordModalOpen(true)}
                    >
                      Change Password
                    </Button>
                  </div>
                </div>
                <div className={classes.formColumn}>
                  <div className={classes.control}>
                    <TextFieldWrapper
                      label="CITY*"
                      inputLabelProps={{ className: classes.inputField }}
                      form={formProps}
                      formKey="city"
                      type="text"
                      variant="outlined"
                    />
                  </div>
                  <div className={classes.smallControlsContainer}>
                    <div className={classes.smallControls}>
                      <TextFieldWrapper
                        variant="outlined"
                        label="STATE*"
                        inputLabelProps={{ className: classes.inputField }}
                        form={formProps}
                        formKey="state"
                        select
                      >
                        {states.map((state) => (
                          <MenuItem key={state.id} value={state.url}>
                            {state.abbrev}
                          </MenuItem>
                        ))}
                      </TextFieldWrapper>
                    </div>
                  </div>
                </div>
                <div className={classes.formColumn}>
                  <div className={classes.control}>
                    <TextFieldWrapper
                      label="INSTITUTION NAME"
                      inputLabelProps={{ className: classes.inputField }}
                      form={formProps}
                      formKey="institutionName"
                      type="text"
                      variant="outlined"
                    />
                  </div>
                  <div className={classes.buttons}>
                    <Button
                      tabIndex={-1}
                      className={classes.button}
                      type="button"
                      variant="contained"
                      color="secondary"
                      onClick={goBack}
                    >
                      Cancel
                    </Button>
                    <Button className={classes.button} type="submit" variant="contained" color="secondary">
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      </div>
      <div>
        <img
          className={classes.images}
          src={`${BASE_URL}${IMAGE_PATHS.TRICOLOR_BAR.path}`}
          alt={`${IMAGE_PATHS.TRICOLOR_BAR.alt}`}
        />
        <HomeFooter />
      </div>
      <PasswordModal isOpen={isPasswordModalOpen} handleModalOpen={(value) => setIsPasswordModalOpen(value)} />
    </Fragment>
  );
};

export default ProfilePage;
